import {plural, t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useCallback, useRef, useState} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import Clickable from "@khanacademy/wonder-blocks-clickable";
import {
    addStyle,
    useForceUpdate,
    useOnMountEffect,
    View,
} from "@khanacademy/wonder-blocks-core";
import {
    MultiSelect,
    OptionItem,
    SingleSelect,
} from "@khanacademy/wonder-blocks-dropdown";
import {TextArea, TextField} from "@khanacademy/wonder-blocks-form";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import SearchField from "@khanacademy/wonder-blocks-search-field";
import {
    border,
    boxShadow,
    semanticColor,
    spacing,
} from "@khanacademy/wonder-blocks-tokens";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import {LabelMedium, LabelSmall} from "@khanacademy/wonder-blocks-typography";
import {isTruthy} from "@khanacademy/wonder-stuff-core";
import {useFastlyQuery} from "@khan/data-gql";
import {
    CONTENT_ITEM_QUERY,
    useContentSearch,
    type getContentSearchResults_searchPage_results_learnableContent_mappedStandards as MappedStandard,
} from "@khan/guide-core";
import {
    getPromptVariable,
    getPromptVariables,
    setPromptVariable,
} from "@khan/guide-core-helpers";
import {
    CUSTOM_CONTENT_MARKER,
    useContentItemPromptVariables,
} from "@khan/guide-teacher-tools";
import type {RequestedInput as RequestedInputType} from "@khan/guide-types";
import {RequestInfo} from "@khan/shared-core";
import {useLocation} from "@khan/urls";

import ClickableContentCell from "./clickable-content-cell.tsx";

import infoIcon from "@phosphor-icons/core/regular/info.svg";
import searchIcon from "@phosphor-icons/core/regular/magnifying-glass.svg";
import minusCircle from "@phosphor-icons/core/regular/minus-circle.svg";
import plusCircle from "@phosphor-icons/core/regular/plus-circle.svg";
import sparkleIcon from "@phosphor-icons/core/regular/sparkle.svg";

const StyledSpan = addStyle("span");

interface Props {
    input: RequestedInputType;
    disabled?: boolean;
    onChange?: (promptVariable: string, newValue: string) => void;
}

const ROWS_IN_TEXT_AREA = 3;

export const RequestedInput = (props: Props): React.ReactElement => {
    const {input, disabled, onChange} = props;

    const {
        label,
        description,
        initialValue = "",
        variableName,
        required,
        inputKind,
        selectOptions = [],
        maxCharacters,
        maxFields,
        selectConfig,
    } = input;

    useOnMountEffect(() => {
        if (initialValue) {
            setValueAndPromptVariable(initialValue);
        }
    });

    let id = `khanmigoInput:${variableName}`;

    const [value, setValue] = React.useState<string>(
        getPromptVariable(variableName) || "",
    );

    const setValueAndPromptVariable = React.useCallback(
        (newValue: string) => {
            setPromptVariable(variableName, newValue);
            setValue(getPromptVariable(variableName) || "");
            onChange?.(variableName, newValue);
        },
        [variableName, onChange],
    );

    const [inputError, setInputError] = React.useState<
        string | null | undefined
    >(null);

    let inputElement;
    if (inputKind === "TEXT_AREA") {
        const validateText: (value: string) => string | null | void = (
            value,
        ) => {
            if (!maxCharacters || value.length <= maxCharacters) {
                return null;
            }
            return t`Uh oh! Input text is ${plural(
                value.length - maxCharacters,
                {
                    one: "# character",
                    other: "# characters",
                },
            )} too long.`;
        };
        const onValidateText: (errorMessage?: string | null) => unknown = (
            errorMsg,
        ) => {
            setInputError(errorMsg);
        };
        inputElement = (
            <TextArea
                id={id}
                value={value}
                onChange={setValueAndPromptVariable}
                disabled={disabled}
                aria-details={description}
                rows={ROWS_IN_TEXT_AREA}
                required={required ? t`Must be provided` : ""}
                resizeType="vertical"
                style={styles.textArea}
                validate={validateText}
                onValidate={onValidateText}
            />
        );
    } else if (inputKind === "SINGLE_SELECT") {
        inputElement = (
            <SingleSelect
                id={id}
                selectedValue={value}
                onChange={setValueAndPromptVariable}
                placeholder={t`Select one...`}
                disabled={disabled}
                aria-required={required}
                aria-details={description}
                style={styles.select}
                isFilterable={selectConfig?.allowFiltering ?? false}
            >
                {selectOptions.map((o, i) => (
                    <OptionItem value={o.value} label={o.label} key={i} />
                ))}
            </SingleSelect>
        );
    } else if (inputKind === "MULTI_SELECT") {
        const labels = selectConfig?.labels;
        inputElement = (
            <MultiSelect
                id={id}
                selectedValues={value ? value.split(", ") : []}
                onChange={(selectedValues: Array<string>) =>
                    setValueAndPromptVariable(selectedValues.join(", "))
                }
                disabled={disabled}
                aria-required={required}
                aria-details={description}
                implicitAllEnabled={selectConfig?.implicitAll ?? false}
                isFilterable={selectConfig?.allowFiltering ?? false}
                shortcuts={selectConfig?.includeShortcuts ?? false}
                labels={
                    labels
                        ? {
                              noneSelected: labels.noneSelected,
                              someSelected: (numSelectedValues) =>
                                  `${numSelectedValues} ${labels.someSelected}`,
                              allSelected: labels.allSelected,
                              selectAllLabel: (numOptions) =>
                                  `${labels.selectAll} (${numOptions})`,
                              selectNoneLabel: labels.selectNone,
                              filter: labels.filter,
                              clearSearch: labels.clearSearch,
                              noResults: labels.noResults,
                          }
                        : undefined
                }
                style={styles.select}
            >
                {selectOptions.map((o, i) => (
                    <OptionItem value={o.value} label={o.label} key={i} />
                ))}
            </MultiSelect>
        );
    } else if (inputKind === "GRID_SELECT") {
        inputElement = (
            <RequestedInputGridSelect
                id={id}
                selectedValue={
                    value || (selectOptions ? selectOptions[0].value : "")
                }
                onChange={setValueAndPromptVariable}
                disabled={disabled}
                options={selectOptions}
            />
        );
    } else if (inputKind === "CONTENT_SEARCH") {
        inputElement = (
            <RequestedInputContentSearch
                id={id}
                value={value}
                onChange={setValueAndPromptVariable}
                disabled={disabled}
            />
        );

        // NOTE(reid): This is needed because the WB SearchField appends a
        // "-field" to the `id` it puts on the text input - so we update the
        // `id` for the label's htmlFor to match.
        id = id + "-field";
    } else if (inputKind === "DYNAMIC_TEXT_FIELDS") {
        const validateText: (value: string) => string | null | void = (
            value,
        ) => {
            if (!maxCharacters || value.length <= maxCharacters) {
                return null;
            }
            return t`Uh oh! Input text is ${plural(
                value.length - maxCharacters,
                {
                    one: "# character",
                    other: "# characters",
                },
            )} too long.`;
        };
        const onValidateText: (errorMessage?: string | null) => unknown = (
            errorMsg,
        ) => {
            setInputError(errorMsg);
        };
        inputElement = (
            <RequestedInputDynamicTextFields
                value={value}
                onChange={setValueAndPromptVariable}
                maxFields={maxFields}
                validate={validateText}
                onValidate={onValidateText}
            />
        );
    } else {
        inputElement = (
            <TextField
                id={id}
                value={value}
                onChange={setValueAndPromptVariable}
                aria-details={description}
                disabled={disabled}
                required={required ? t`Must be provided` : ""}
                autoComplete="off"
            />
        );
    }

    return (
        <View style={styles.row}>
            <LabelSmall tag="label" htmlFor={id} style={styles.label}>
                {label}
                {required && (
                    <StyledSpan
                        style={styles.requiredMarker}
                        aria-label={t`Required`}
                    >
                        *
                    </StyledSpan>
                )}
            </LabelSmall>
            {label && <Strut size={spacing.xxxSmall_4} />}
            <LabelSmall style={styles.description}>{description}</LabelSmall>
            {description && <Strut size={spacing.xxSmall_6} />}
            {inputElement}
            {inputError && (
                <>
                    <Strut size={spacing.xxxSmall_4} />
                    <LabelSmall
                        style={styles.error}
                        role="alert"
                        aria-live="assertive"
                    >
                        {inputError}
                    </LabelSmall>
                </>
            )}
        </View>
    );
};

const WB_INPUT_HEIGHT = 80;

const styles = StyleSheet.create({
    row: {
        width: "100%",
        marginBlockEnd: spacing.large_24,
    },
    label: {
        fontWeight: "bold",
        fontSize: spacing.medium_16,
    },
    description: {
        color: semanticColor.core.foreground.neutral.subtle,
    },
    requiredMarker: {
        color: semanticColor.core.foreground.critical.default,
    },
    select: {
        width: "100%",
    },
    textArea: {
        minBlockSize: WB_INPUT_HEIGHT,
    },
    error: {
        borderRadius: spacing.xxxSmall_4,
        height: "100%",
        alignItems: "center",
        justifyContent: "center",
        padding: spacing.small_12,
        background: semanticColor.core.background.critical.subtle,
        border: `1px solid ${semanticColor.core.border.critical.subtle}`,
    },
});

interface GridSelectProps {
    id: string;
    selectedValue: string;
    onChange: (newValue: string) => void;
    options: RequestedInputType["selectOptions"];
    required?: boolean;
    disabled?: boolean;
}

export function RequestedInputGridSelect(
    props: GridSelectProps,
): React.ReactElement {
    const {
        id,
        selectedValue,
        onChange,
        options = [],
        required,
        disabled,
    } = props;

    const rerender = useForceUpdate();
    const ref = useRef<HTMLDivElement>(null);

    const selectedOption = options.find((o) => o.value === selectedValue);
    const additionalInputs = selectedOption?.additionalInputs;

    useOnMountEffect(() => {
        if (options.length) {
            onChange(options[0].value);
        }
    });

    const selectOption = useCallback(
        (index: number) => {
            onChange(options[index].value);

            // NOTE(reid): nth-child uses 1-based indexing, so we add 1 here
            const optionElement = ref.current!.querySelector<HTMLElement>(
                `[role=radio]:nth-child(${index + 1})`,
            );
            if (optionElement) {
                optionElement.focus();
            }
        },
        [onChange, options],
    );

    const handleKeyDown = useCallback(
        (evt: React.KeyboardEvent) => {
            if (evt.key === "ArrowDown" || evt.key === "ArrowRight") {
                evt.preventDefault();
                const selectedIndex = options.findIndex(
                    (o) => o.value === selectedValue,
                );
                const newSelectedIndex = (selectedIndex + 1) % options.length;
                selectOption(newSelectedIndex);
            } else if (evt.key === "ArrowUp" || evt.key === "ArrowLeft") {
                evt.preventDefault();
                const selectedIndex = options.findIndex(
                    (o) => o.value === selectedValue,
                );
                const newSelectedIndex =
                    selectedIndex === 0
                        ? options.length - 1
                        : selectedIndex - 1;
                selectOption(newSelectedIndex);
            }
        },
        [options, selectedValue, selectOption],
    );

    return (
        <View>
            <View
                style={gridSelectStyles.container}
                id={id}
                role="radiogroup"
                aria-required={required}
                aria-disabled={disabled}
                ref={ref}
            >
                {options.map((option) => {
                    const isSelected = option.value === selectedValue;

                    return (
                        <Clickable
                            key={option.value}
                            disabled={disabled}
                            role="radio"
                            aria-checked={isSelected}
                            aria-details={option.tooltip}
                            onClick={() => onChange(option.value)}
                            onKeyDown={handleKeyDown}
                            tabIndex={isSelected ? 0 : -1}
                            style={[
                                gridSelectStyles.option,
                                disabled && gridSelectStyles.disabled,
                                isSelected && gridSelectStyles.selected,
                            ]}
                        >
                            {() => (
                                <View style={gridSelectStyles.inner}>
                                    <View style={gridSelectStyles.labelRow}>
                                        <LabelMedium>
                                            {option.label}
                                        </LabelMedium>
                                        {option.tooltip && (
                                            <Tooltip content={option.tooltip}>
                                                <PhosphorIcon
                                                    icon={infoIcon as any}
                                                />
                                            </Tooltip>
                                        )}
                                    </View>
                                    {option.description && (
                                        <LabelSmall
                                            style={
                                                gridSelectStyles.optionDescription
                                            }
                                        >
                                            {option.description}
                                        </LabelSmall>
                                    )}
                                </View>
                            )}
                        </Clickable>
                    );
                })}
            </View>
            {additionalInputs && (
                <RequestedInputs
                    key={id + selectedValue}
                    inputs={additionalInputs}
                    disabled={disabled}
                    onChange={rerender}
                />
            )}
        </View>
    );
}

const gridSelectStyles = StyleSheet.create({
    container: {
        display: "grid",
        gap: spacing.small_12,
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    },
    option: {
        border: `1px solid ${semanticColor.core.border.neutral.default}`,
        padding: spacing.small_12,
        borderRadius: border.radius.radius_040,
        textAlign: "start",
        display: "flex",
    },
    inner: {
        width: "100%",
    },
    selected: {
        background: semanticColor.core.background.instructive.subtle,
        color: semanticColor.core.foreground.instructive.subtle,
    },
    disabled: {
        border: `1px solid ${semanticColor.input.disabled.border}`,
        background: semanticColor.input.disabled.background,
        color: semanticColor.input.disabled.foreground,
    },
    labelRow: {
        width: "100%",
        flexDirection: "row",
        justifyContent: "space-between",
    },
    optionDescription: {
        color: semanticColor.core.foreground.neutral.subtle,
        marginBlockStart: spacing.xxxxSmall_2,
        marginBlockEnd: spacing.xLarge_32,
    },
});

interface DynamicTextFieldProps {
    value: string;
    onChange: (newValue: string) => void;
    maxFields?: number;
    validate?: (value: string) => string | null | void;
    onValidate?: (errorMessage?: string | null) => unknown;
}

interface RequestedInputSingleFieldProps {
    index: number;
    value: string;
    onChange: (index: number, newValue: string) => void;
    removeField: () => void;
}

export function RequestedInputDynamicTextFields(
    props: DynamicTextFieldProps,
): React.ReactElement {
    const {onChange, value, maxFields, validate, onValidate} = props;

    const [fields, setFields] = useState<string[]>(
        value ? JSON.parse(value) : [""],
    );

    const shouldHideAdd =
        maxFields &&
        (fields.length === maxFields || fields.length >= maxFields);

    const onRemoveField = (index: number) => {
        const copyFields = [...fields];
        copyFields.splice(index, 1);
        const newFields = copyFields.length < 1 ? [""] : copyFields;
        setFields(newFields);
        onChange(JSON.stringify(newFields));
    };

    return (
        <View style={dynamicTextFieldStyles.container}>
            <View style={dynamicTextFieldStyles.fieldList}>
                {fields.map((fieldValue, index) => {
                    return (
                        <RequestedInputSingleField
                            key={index}
                            index={index}
                            value={fieldValue}
                            onChange={(index, newValue) => {
                                const error = validate
                                    ? validate(newValue)
                                    : null;
                                if (error) {
                                    onValidate?.(error);
                                }
                                const copyFields = [...fields];
                                copyFields[index] = newValue;
                                setFields(copyFields);
                                onChange(JSON.stringify(copyFields));
                            }}
                            removeField={() => {
                                onRemoveField(index);
                            }}
                        />
                    );
                })}
            </View>
            <View style={dynamicTextFieldStyles.addAnotherField}>
                <Button
                    style={shouldHideAdd && dynamicTextFieldStyles.hidden}
                    onClick={() => {
                        setFields([...fields, ""]);
                    }}
                    kind="tertiary"
                    startIcon={plusCircle}
                    aria-label={t`Add another`}
                >
                    {t`Add another`}
                </Button>
            </View>
        </View>
    );
}

const RequestedInputSingleField = (props: RequestedInputSingleFieldProps) => {
    const {value, index, onChange, removeField} = props;

    // First field is always shown
    const [showRemoveOption, _] = useState(index !== 0);

    return (
        <View style={dynamicTextFieldStyles.fieldContainer}>
            <TextField
                value={value}
                onChange={(newValue) => {
                    onChange(index, newValue);
                }}
            />
            {showRemoveOption ? (
                <IconButton
                    aria-label={t`Remove field`}
                    kind="tertiary"
                    actionType="destructive"
                    icon={minusCircle}
                    onClick={removeField}
                />
            ) : (
                <IconButton
                    aria-label={t`Remove field`}
                    kind="tertiary"
                    actionType="neutral"
                    icon={minusCircle}
                    disabled={true}
                />
            )}
        </View>
    );
};

const dynamicTextFieldStyles = StyleSheet.create({
    container: {
        width: "100%",
        gap: spacing.xSmall_8,
    },
    fieldContainer: {
        flex: "1",
        flexDirection: "row",
        gap: spacing.medium_16,
        justifyContent: "space-between",
    },
    fieldList: {
        gap: spacing.medium_16,
    },
    addAnotherField: {
        display: "flex",
        width: "100%",
        alignItems: "start",
    },
    hidden: {
        display: "none",
    },
});

type LearnableContentKind =
    | "Article"
    | "Challenge"
    | "Exercise"
    | "Interactive"
    | "Project"
    | "Talkthrough"
    | "Video";

interface ContentSearchProps {
    id: string;
    value: string;
    onChange: (newValue: string) => void;
    disabled?: boolean;
}

export function RequestedInputContentSearch(
    props: ContentSearchProps,
): React.ReactElement {
    const {id, value, onChange, disabled} = props;

    const {query, setQuery, loading, searchResults} = useContentSearch({
        allowedTopicAncestors: ["math", "science", "humanities"],
        contentKinds: ["Article", "Exercise", "Video"],
        numResults: 3,
    });

    const [chosenContent, setChosenContent] = useState<{
        id: string;
        contentDescriptor: string;
        title: string;
        contentKind: LearnableContentKind;
        courseTitle: string;
        description: string;
        standards?: MappedStandard[];
    } | null>(null);

    const [customContent, setCustomContent] = useState<{
        userProvidedTopic: string;
        userProvidedStandards: string;
        userProvidedNotes: string;
    } | null>(null);

    const {unsetContentItemPromptVariables} = useContentItemPromptVariables(
        chosenContent?.contentKind as LearnableContentKind,
        chosenContent?.id || "",
    );

    const {queryParams} = useLocation();

    const alreadySelectedContent = value
        ? value
        : queryParams.contentDescriptor;

    const contentItemResult = useFastlyQuery(CONTENT_ITEM_QUERY, {
        variables: {
            descriptors: [alreadySelectedContent],
            kaRegion: RequestInfo.kaRegion,
        },
        skip: !alreadySelectedContent,
    });

    React.useEffect(() => {
        const {userProvidedTopic, userProvidedStandards, userProvidedNotes} =
            getPromptVariables();

        if (userProvidedTopic) {
            setCustomContent({
                userProvidedTopic,
                userProvidedStandards,
                userProvidedNotes,
            });
            return;
        }

        if (contentItemResult.status === "success") {
            const contentItems = contentItemResult.data.contentForDescriptors;
            if (!contentItems?.[0]) {
                return;
            }

            const contentItem = contentItems[0];

            if (!chosenContent) {
                onChange(contentItem.contentDescriptor ?? "");
                setChosenContent({
                    id: contentItem.id,
                    title: contentItem.translatedTitle ?? "",
                    description: contentItem.translatedDescription ?? "",
                    contentDescriptor: contentItem.contentDescriptor ?? "",
                    contentKind:
                        contentItem.contentKind as LearnableContentKind,
                    courseTitle:
                        contentItem.lesson?.unit?.course?.translatedTitle ?? "",
                    standards: contentItem.mappedStandards?.filter(isTruthy),
                });
            }
        }
    }, [contentItemResult, onChange, setChosenContent, chosenContent]);

    const handleUserProvidedTopicChange = React.useCallback(
        (_: string, value: string) =>
            setCustomContent((content) => ({
                ...content!,
                userProvidedTopic: value,
            })),
        [],
    );

    const handleUserProvidedStandardsChange = React.useCallback(
        (_: string, value: string) =>
            setCustomContent((content) => ({
                ...content!,
                userProvidedStandards: value,
            })),
        [],
    );

    const handleUserProvidedNotesChange = React.useCallback(
        (_: string, value: string) =>
            setCustomContent((content) => ({
                ...content!,
                userProvidedNotes: value,
            })),
        [],
    );

    if (contentItemResult.status === "loading") {
        return <CircularSpinner size="small" />;
    }

    if (chosenContent) {
        return (
            <ClickableContentCell
                chosen
                title={chosenContent.title}
                description={chosenContent.description}
                contentDescriptor={chosenContent.contentDescriptor}
                courseTitle={chosenContent.courseTitle}
                mappedStandards={chosenContent.standards}
                onClick={() => {
                    unsetContentItemPromptVariables();
                    setQuery("");
                    setChosenContent(null);
                    onChange("");
                }}
                disabled={disabled}
            />
        );
    }

    if (customContent) {
        return (
            <View>
                <RequestedInput
                    disabled={disabled}
                    onChange={handleUserProvidedTopicChange}
                    input={{
                        inputKind: "TEXT_BOX",
                        initialValue: customContent.userProvidedTopic,
                        variableName: "userProvidedTopic",
                        label: "",
                        description: "",
                    }}
                />
                <RequestedInput
                    disabled={disabled}
                    onChange={handleUserProvidedStandardsChange}
                    input={{
                        inputKind: "TEXT_AREA",
                        required: true,
                        initialValue: customContent.userProvidedStandards,
                        variableName: "userProvidedStandards",
                        label: t`Standards or learning objective`,
                        description: t`Provide a full standard description or learning objective associated with your topic.`,
                    }}
                />
                <RequestedInput
                    disabled={disabled}
                    onChange={handleUserProvidedNotesChange}
                    input={{
                        inputKind: "TEXT_AREA",
                        initialValue: customContent.userProvidedNotes,
                        variableName: "userProvidedNotes",
                        required: true,
                        label: t`Notes`,
                        description: t`Add any details about the topic or teaching tools you'd like to use`,
                    }}
                />
                <Button
                    disabled={disabled}
                    startIcon={searchIcon}
                    kind="tertiary"
                    style={{width: "fit-content"}}
                    onClick={() => {
                        unsetContentItemPromptVariables();
                        setCustomContent(null);
                        setChosenContent(null);
                        setPromptVariable("userProvidedTopic", null);
                        setPromptVariable("userProvidedStandards", null);
                        setPromptVariable("userProvidedNotes", null);
                        setQuery("");
                        onChange("");
                    }}
                >
                    {t`Use Khan Academy content instead`}
                </Button>
            </View>
        );
    }

    return (
        <View style={contentSearchStyles.container}>
            <SearchField
                id={id}
                value={query}
                onChange={setQuery}
                placeholder={t`Search Khan Academy...`}
            />
            {query.length > 0 && (
                <View style={contentSearchStyles.results}>
                    {loading && (
                        <View style={contentSearchStyles.loading}>
                            <CircularSpinner size="small" />
                            <Strut size={spacing.medium_16} />
                            <LabelMedium>{t`Loading...`}</LabelMedium>
                        </View>
                    )}
                    {!loading &&
                        searchResults.map((result) => {
                            const learnableContent = result?.learnableContent;
                            if (!learnableContent) {
                                return <></>;
                            }

                            const title =
                                learnableContent.translatedTitle || "";
                            const description =
                                learnableContent.translatedDescription || "";

                            const contentDescriptor =
                                learnableContent.contentDescriptor || "";
                            const contentKind = (result.kind ||
                                "") as LearnableContentKind;
                            const course = [
                                learnableContent.parentTopic,
                                learnableContent.parentTopic?.parent,
                                learnableContent.parentTopic?.parent?.parent,
                            ].find(
                                (ancestor) =>
                                    ancestor?.contentKind === "Course",
                            );
                            const courseTitle = course?.translatedTitle || "";

                            const standards =
                                learnableContent.mappedStandards?.filter(
                                    isTruthy,
                                );

                            return (
                                <ClickableContentCell
                                    key={result.contentId}
                                    title={title}
                                    description={description}
                                    mappedStandards={standards}
                                    contentDescriptor={contentDescriptor}
                                    courseTitle={courseTitle}
                                    onClick={() => {
                                        unsetContentItemPromptVariables();
                                        onChange(contentDescriptor);
                                        setQuery("");
                                        setCustomContent(null);
                                        setChosenContent({
                                            id: result.contentId || "",
                                            title,
                                            description,
                                            contentDescriptor,
                                            contentKind,
                                            courseTitle,
                                            standards,
                                        });
                                    }}
                                />
                            );
                        })}
                    <ClickableContentCell
                        title={t`Provide your own content...`}
                        description={t`Khanmigo will need a few details to help you write`}
                        courseTitle=""
                        contentDescriptor=""
                        onClick={() => {
                            unsetContentItemPromptVariables();
                            onChange(CUSTOM_CONTENT_MARKER);
                            setQuery("");
                            setChosenContent(null);
                            setCustomContent({
                                userProvidedTopic: query,
                                userProvidedStandards: "",
                                userProvidedNotes: "",
                            });
                        }}
                        icon={sparkleIcon}
                    />
                </View>
            )}
        </View>
    );
}

const contentSearchStyles = StyleSheet.create({
    container: {
        position: "relative",
    },
    results: {
        marginBlockStart: -spacing.xxxxSmall_2,
        border: `1px solid ${semanticColor.core.border.neutral.subtle}`,
        width: "100%",
        boxShadow: boxShadow.mid,
        borderRadius: spacing.xxxSmall_4,
    },
    loading: {
        flexDirection: "row",
        padding: spacing.medium_16,
        color: semanticColor.core.foreground.neutral.subtle,
        borderBlockEnd: `1px solid ${semanticColor.core.border.neutral.subtle}`,
    },
});

interface RequestedInputsProps {
    inputs: ReadonlyArray<RequestedInputType>;
    disabled?: boolean;
    onChange?: (promptVariable: string, newValue: string) => void;
}

export const RequestedInputs = (
    props: RequestedInputsProps,
): React.ReactElement => {
    const {inputs, disabled, onChange} = props;

    return (
        <View style={[requestedInputsStyles.row]}>
            {inputs.map((input, index) => (
                <RequestedInput
                    input={input}
                    disabled={disabled}
                    onChange={onChange}
                    key={index}
                />
            ))}
        </View>
    );
};

const requestedInputsStyles = StyleSheet.create({
    row: {
        marginBlockStart: spacing.medium_16,
        flexFlow: "row wrap",
    },
});
